import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { Link, useLoaderData, useParams } from "@remix-run/react";
import { List } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DocSidebarNav } from "#app/components/docs/DocSidebarNav";
import { DocTutorialShell } from "#app/components/docs/DocTutorialShell";
import { OnThisPage } from "#app/components/docs/OnThisPage";
import { MarkdownContent } from "#app/components/markdown/MarkdownContent";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "#app/components/ui/breadcrumb";
import { Button } from "#app/components/ui/button";
import { getRequestLocale } from "#app/i18n/get-request-locale.server";
import { loadDoc, loadDocIndex } from "#app/lib/docs/loader";
import { extractSteps } from "#app/lib/tutorials/parser";

export const handle = { i18n: ["docs", "common"] };

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.doc) {
    return [
      {
        title:
          data?.locale === "ja"
            ? "ドキュメント | Supervity"
            : "Documentation | Supervity",
      },
    ];
  }
  const tags: Array<{ title?: string; name?: string; content?: string }> = [
    { title: `${data.doc.metadata.title} | Supervity Docs` },
  ];
  if (data.doc.metadata.description) {
    tags.push({ name: "description", content: data.doc.metadata.description });
  }
  return tags;
};

export async function loader({ request, params }: LoaderFunctionArgs) {
  const locale = await getRequestLocale(request);
  const splat = params["*"];
  const slug = splat?.replace(/\/+$/g, "");
  if (!slug) throw new Response("Document not found", { status: 404 });

  const [doc, docs] = await Promise.all([
    loadDoc(slug, locale),
    loadDocIndex(locale),
  ]);
  if (!doc) throw new Response("Document not found", { status: 404 });

  const appOrigin = process.env.APP_ORIGIN_URL || "http://localhost:3000";
  doc.content = doc.content.replace(/\{\{APP_ORIGIN_URL\}\}/g, appOrigin);

  const headings = extractSteps(doc.content).filter((h) => h.level >= 2);
  return json({ doc, docs, headings, locale });
}

export default function DocViewerSplat() {
  const { t } = useTranslation("docs");
  const { doc, docs, headings } = useLoaderData<typeof loader>() as any;
  const params = useParams();
  const slug = params["*"] || "";
  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  const contentWithoutDuplicateTitle = (() => {
    const title = String(doc?.metadata?.title ?? "").trim();
    const content = String(doc?.content ?? "");
    if (!title || !content) return content;

    // If the markdown starts with an H1 that matches the frontmatter title,
    // remove it because we already render the title above.
    const match = content.match(/^\s*#\s+(.+?)\s*\n(?:\s*\n)?/);
    if (!match) return content;

    const h1 = match[1]?.trim() ?? "";
    if (h1.localeCompare(title, undefined, { sensitivity: "base" }) !== 0) {
      return content;
    }

    return content.slice(match[0].length);
  })();

  return (
    <DocTutorialShell
      activeTab="docs"
      leftNav={<DocSidebarNav docs={docs} currentSlug={doc.slug} />}
      rightRail={<OnThisPage headings={headings} />}
    >
      <div className="space-y-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/docs">{t("documentation")}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{doc.metadata.title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="space-y-1">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#141A42] dark:text-zinc-100">
            {doc.metadata.title}
          </h1>
          {doc.metadata.description ? (
            <p className="text-muted-foreground">{doc.metadata.description}</p>
          ) : null}
        </div>

        {headings.length > 0 && (
          <div className="xl:hidden">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full justify-start gap-2"
              onClick={() => setMobileTocOpen((open) => !open)}
              aria-expanded={mobileTocOpen}
            >
              <List className="h-4 w-4" />
              {t("onThisPage")}
            </Button>
            {mobileTocOpen && (
              <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <OnThisPage headings={headings} />
              </div>
            )}
          </div>
        )}

        <MarkdownContent content={contentWithoutDuplicateTitle} />

        {/* End-of-content marker so scroll logic can identify where doc content ends */}
        <div
          id="docs-content-end"
          data-docs-content-end
          className="h-0 w-full shrink-0"
          aria-hidden
        />

        {/* slug is currently unused but kept for future: contextual search/assistant */}
        <div className="sr-only">{slug}</div>
      </div>
    </DocTutorialShell>
  );
}
